import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { useCampaignResults } from '../hooks/useCampaignResults';
import { Funnel } from './Funnel';
import { DailyChart } from './DailyChart';
import { ActivityFeed } from './ActivityFeed';
import { resultsMessages as messages } from './resultsMessages';
import { StreamsChart } from './StreamsChart';
import { WarmPeople } from './WarmPeople';
import { LinkClicks } from './LinkClicks';
import { AddsTable } from './AddsTable';
import { WhatWorked } from './WhatWorked';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import card from '../live/Campaign.module.scss';
import styles from './Results.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Results tab of a campaign (design outreach-v2 t6, n1): the totals, the pitches day by day,
 * what happened, the streams with the adds, the warm people, the pitch page clicks, the adds and
 * what worked. The first week shows all of it with one note that most replies and adds come later
 * (`tooEarly`); "What worked" says itself when a group is too small.
 */
const CampaignResultsTabComponent: React.FC<{ accountId: number; campaignUuid: string }> = ({
  accountId,
  campaignUuid,
}) => {
  const intl = useIntl();
  const { results, isLoading, error, retry, createFromWarm, isCreatingFromWarm } = useCampaignResults(
    accountId,
    campaignUuid
  );
  if (isLoading) {
    return <LoadingView />;
  }
  if (error !== null || results === undefined) {
    return <ErrorView error={error ?? undefined} retry={retry} />;
  }
  return (
    <div className={styles.stack} data-testid="campaign-results">
      <Funnel funnel={results.funnel} dayNumber={results.dayNumber} songTitle={results.songTitle} />
      {results.tooEarly && (
        <p className={`${card.cardIntro} m-0`} data-testid="results-too-early">
          {intl.formatMessage(messages.tooEarlyBody)}
        </p>
      )}
      {/* An API from before `daily` answers none: the chart says it starts with the first pitch. */}
      <DailyChart days={results.daily ?? []} />
      <ActivityFeed accountId={accountId} campaignUuid={campaignUuid} />
      <div className={styles.grid2}>
        <StreamsChart
          songTitle={results.songTitle}
          streams={results.streams}
          source={results.streamsSource}
          pins={results.pins}
          releaseDate={results.releaseDate}
        />
        <div className={card.col}>
          <WarmPeople results={results} createFromWarm={createFromWarm} isCreating={isCreatingFromWarm} />
          <LinkClicks accountId={accountId} campaignUuid={campaignUuid} />
        </div>
      </div>
      <AddsTable adds={results.adds} />
      <WhatWorked
        byKind={results.byKind}
        bySize={results.bySize}
        byAngle={results.byAngle}
        learned={results.learned}
        songTitle={results.songTitle}
      />
    </div>
  );
};

export const CampaignResultsTab = TypedMemo(CampaignResultsTabComponent);
